import React, { useContext, useEffect, useState } from "react";
import { Alert, Button, Calendar, message, Select, Skeleton, Space, Tag, Timeline, TimePicker} from "antd";
import { reservation_response, reserveState, room, RoomReservation, Types } from "../utils/interfaces";
import moment, { Moment } from "moment-timezone";
import { get_string } from "../i18n/strings";
import { Context } from "../utils/store";
import Axios, { AxiosError } from "axios";
import { apiUrl } from "../utils/config";
import { DeleteOutlined } from '@ant-design/icons'
import { handleTokenExpired } from "../utils/utils";
import '../styles/InDayReservation.css'
import 'moment/locale/sk'
import 'moment/locale/en-gb'
import skSK from 'antd/lib/calendar/locale/sk_SK';
import enGB from 'antd/lib/calendar/locale/en_GB';
moment.tz.setDefault('Europe/Bratislava')
const { Option } = Select;
interface Props {
  room: room
}

type Quota = {
  used_hours: number,
  used_minutes: number,
  used_seconds_total: number
}

const minuteStep = 15;
const disabledStartHours = [0, 1, 2, 3, 4, 5, 22, 23];
const disabledEndHoursBase = [0, 1, 2, 3, 4, 5, 6, 23];
const LATEST_TIME = moment("22:00", "H:m");

export default function InDayReservation({ room }: Props) {
  const [startTime, setStartTime] = useState<Moment | null>(null);
  const [endTime, setEndTime] = useState<Moment | null>(null);
  const [pickedDate, setPickedDate] = useState<Moment>(moment());
  const [interval, setInterval] = useState<Moment>(moment("01:30", "H:m"));
  const [disableTime, setDisableTime] = useState(false);
  const [disabledHoursForStart, setDisabledHoursForStart] = useState<number[]>([]);
  const [disabledHoursForEnd, setDisabledHoursForEnd] = useState<number[]>([]);
  const [reserveState, setReserveState] = useState<reserveState>('not_reserved'); //just trigger to refetch reservations
  const { state, dispatch } = useContext(Context)
  const [reservationList, setReservationList] = useState<RoomReservation[]>();
  const [usedQuota, setUsedQuota] = useState<Quota | null>(null);

  // get reservations for room.id when id or pickedDate or reserveStatus changed
  useEffect(() => {
    if (room.start === 'None') {
      room.start = '06:00:00'
    } else if (room.end === 'None') {
      room.end = '22:00:00'
    }
    const pickedDateCopy = pickedDate;

    pickedDateCopy.set({ hour: 22, minute: 0, second: 0 }).unix();

    const start = moment(room.start, "H:m:s");
    console.log(start);
    const unixStartTime = pickedDateCopy.set({ hour: start.hour(), minutes: start.minute(), second: start.second() }).unix();
    console.log(unixStartTime);

    const end = moment(room.end, "H:m:s");
    const unixEndTime = pickedDateCopy.set({ hour: end.hour(), minutes: end.minute(), second: end.second() }).unix();


    Axios.get<RoomReservation[]>(`${apiUrl}/reservation/find/?roomid=${room.id}&start=${unixStartTime}&end=${unixEndTime}`, {
      headers: {
        'Authorization': `JWT ${state.token}`
      }
    })
      .then(res => {
        setReservationList(res.data.sort((a, b) => a.start - b.start))
      })
      .catch((e : AxiosError) => {
        const isExpired = handleTokenExpired(e.response?.status, ()=>{dispatch({ type: Types.Logout, payload: null})})

          console.log(e.response?.status);
          console.log(e.response);;

        })
  }, [room.id, pickedDate, reserveState])


  // set disabled hours for start time
  useEffect(() => {
    const today = moment();
    const isPastDate = today.isAfter(pickedDate, 'date') || (today.isSame(pickedDate, "date") && today.hour() >= 22);
    setDisableTime(isPastDate);

    if (isPastDate) return;

    const hours = [...disabledStartHours];
    if (today.date() === pickedDate.date()) {
      for (let i = 6; i < today.hour(); i++) {
        hours.push(i);
      }
    }
    setDisabledHoursForStart(hours.sort((a, b) => a - b));
  }, [pickedDate]);

  // set disabled hours for end time
  useEffect(() => {
    const hours = [...disabledEndHoursBase];
    if (startTime && moment().date() == startTime.date()) {
      for (let i = 7; i < startTime.hour(); i++) hours.push(i);
    }
    setDisabledHoursForEnd(hours);
  }, [disabledHoursForStart, startTime]);

  // set values on date load
  useEffect(() => {
    const now = moment();

    if (!now.isBefore(pickedDate, "date") && now.hour() >= 22) {
      setStartTime(null);
      return;
    } else if (now.hour() < 6 || now.hour() > 21) {
      setStartTime(moment("8:30", "HH:mm"))
      return;
    }
    let minutes = now.minute();
    if (minutes > 45) minutes = 0;
    else if (minutes > 30) minutes = 45;
    else if (minutes > 15) minutes = 30;
    else if (minutes > 0) minutes = 15;

    setStartTime(moment(`${now.hour()}:${minutes}`, "HH:mm"));
  }, [pickedDate]);

  // calculate interval
  useEffect(() => {
    if (startTime && endTime) {
      const duration = endTime.diff(startTime, 'minute');
      setInterval(moment().startOf('day').add(duration, 'minute'));
    } else {
      setInterval(moment("01:30", "HH:mm"));
    }
  }, [endTime]);

  // automatically sets end time based on interval and start time
  useEffect(() => {
    if (!startTime || !interval) return;
    const added = startTime.clone().add(interval.hour(), "hour").add(interval.minute(), "minute");
    setEndTime(added.isAfter(LATEST_TIME) ? LATEST_TIME : added);
  }, [startTime]);

  // functions to control time selection
  const onStartTimeChange = (time: Moment) => setStartTime(time)
  const onEndTimeChange = (time: Moment) => {
    if (!time || !startTime) return;
    const isSame = time.isSame(startTime);
    const isBefore = time.isBefore(startTime);

    if (!isSame) {
      isBefore ? setStartTime(time) : setEndTime(time);
    }
  }

  const handleDelete = (reservationId: number) => {
    Axios.delete(`${apiUrl}/reservation/${reservationId}`, {
      headers: { 'Authorization': `JWT ${state.token}` }
    })
      .then(res => {
        message.success(res.data.result);
        setReserveState('deleted');
      })
      .catch(e => {
        const isExpired = handleTokenExpired(e.response?.status, ()=>{dispatch({ type: Types.Logout, payload: null})})
        if(!isExpired){
          message.error(e.response.data.result);
        }
      })
  }

  const handleReserve = () => {
    if (!startTime || !endTime) return;

    const start = startTime;
    start.set({ date: pickedDate.date(), month: pickedDate.month(), year: pickedDate.year() })

    const end = endTime;
    end.set({ date: pickedDate.date(), month: pickedDate.month(), year: pickedDate.year() });


    Axios.post<reservation_response>(`${apiUrl}/reservation/1`, {
      roomid: room.id,
      start: start.unix(),
      end: end.unix()
    }, {
      headers: {
        'Authorization': `JWT ${state.token}`
      }
    })
      .then(res => {
        if (res.data.result_id === "reservation_ok") {
          message.success(get_string(state.locale, res.data.result_id))
          setReserveState('reserved');
        }

      })
      .catch((e: AxiosError) => {
        console.log(e.response?.data);
        const isExpired = handleTokenExpired(e.response?.status, ()=>{dispatch({ type: Types.Logout, payload: null})})
        if(!isExpired){
          message.error(get_string(state.locale, e.response?.data.result_id))
          setReserveState('error');
        }
      });
  }

  // get reservation status by checking its end time with current time
  const getStatus = (el: RoomReservation): 'processing' | 'success' | 'default' => {
    const time = moment().unix();
    if (time > el.start && time < el.end) {
      return 'processing';
    }
    if (time > el.end) {
      return 'success';
    }
    if (time < el.start) {
      return 'default';
    }
    return 'default'
  }

  // get actual user quote
  const getQuote = () => {
    Axios.get<Quota>(`${apiUrl}/api/quota/used/`,{
      headers: {
        'Authorization': `JWT ${state.token}`
      }
    })
    .then((response) => setUsedQuota(response.data))

  }


  if (reservationList === undefined) {
    return <Skeleton loading />
  }

  return (
    <>
      <div className="site-calendar-demo-card">
        <div className="site-calendar-card">
          <Calendar locale={state.locale == 'sk' ? skSK: enGB} onChange={(date: any) => setPickedDate(date)} fullscreen={false} />
        </div>
      </div>

      <div className="current-rezervations">
        {reservationList.length > 0 ?
          (<Timeline>
            {reservationList.map((el) => (
              <Timeline.Item key={el.id}>
                {el.user !== '_User_not_found' ? el.user.firstname + " " + el.user.lastname + " " : 'User not found '}
                <Tag color={getStatus(el)}>{moment(el.start, 'X').format('H:mm')} {moment(el.end, 'X').format('H:mm')}</Tag>
              {getStatus(el) == 'default' &&
                el.user !== '_User_not_found' &&
                state.user_id === el.user.id &&
                <Button danger type='link' onClick={e => handleDelete(el.id)} icon={<DeleteOutlined />} />}
            </Timeline.Item>
          )
          )}
        </Timeline>)
        : (<Alert message={get_string(state.locale, 'no_reservations')} type="info" />)}
      </div>

      <div className="time-select">
        <div className="time-strings">
          <h4>{get_string(state.locale, "start_time")}</h4>
          <h4>{get_string(state.locale, "end_time")}</h4>
        </div>
        <Space className="mb-10px">
          <TimePicker
              allowClear={false}
              size={'large'}
              showNow={false}
              disabledHours={() => disabledHoursForStart }
              disabled={disableTime}
              format={'HH:mm'}
              minuteStep={minuteStep}
              value={startTime}
              hideDisabledOptions={true}
              onOk={(date: any) => onStartTimeChange(date)}
              inputReadOnly={true}
              popupClassName={"custom-timepicker-popup"}
          />
          <TimePicker
              allowClear={false}
              size={'large'}
              showNow={false}
              disabledHours={() => disabledHoursForEnd }
              disabled={disableTime}
              format={'HH:mm'}
              minuteStep={minuteStep}
              value={endTime}
              hideDisabledOptions={true}
              onOk={(date: any) => onEndTimeChange(date)}
              inputReadOnly={true}
              popupClassName={"custom-timepicker-popup" }
          />
        </Space>

        <Button disabled={pickedDate.unix() + 3600 < moment().unix()} onClick={handleReserve} type={"primary"} block style={{ marginBottom: 10 }}>
          {get_string(state.locale, "reserve")}
        </Button>

        {/*<Alert
          message={"Quote left: 6h / 12h"}
          type="info"
          style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}
        />*/}
          
      </div>
    </>
  );
}
